import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Table, Spinner, Badge, OverlayTrigger, Tooltip, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faNetworkWired, faPlus, faSave, faPlay, faTrash, faHistory,
    faDatabase, faClock, faServer, faCheckCircle, faExclamationCircle,
    faCogs, faSpinner, faCode, faFileAlt, faArrowRight, faTable, faBoxOpen, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';
import { motion, AnimatePresence } from 'framer-motion';

const EtlStudioPanel = ({ ipserv }) => {
    const [jobs, setJobs] = useState([]);
    const [connections, setConnections] = useState([]);
    const [queries, setQueries] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedJob, setSelectedJob] = useState(null);
    const [jobLogs, setJobLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [activeTab, setActiveTab] = useState('config');

    // Form State
    const [formData, setFormData] = useState({
        JobId: null,
        JobName: '',
        SourceConnId: '',
        TargetConnId: '',
        SourceQuery: '',
        AutqId: '', // Added support for AutqId
        TargetTable: '',
        OperationType: 'INSERT',
        CronSchedule: '',
        IsActive: true,
        SourceEngine: 'mysql'
    });

    useEffect(() => {
        fetchConnections();
        fetchQueries();
        fetchJobs();
    }, []);

    const fetchConnections = () => {
        request.Get(ipserv + 'superadmin/connections', {}, r => {
            if (r.success) setConnections(r.data);
        });
    };

    const fetchQueries = () => {
        request.Get(ipserv + 'superadmin/queries', {}, r => {
            if (r.success) setQueries(r.data);
        });
    };

    const fetchJobs = () => {
        setLoading(true);
        const adminId = JSON.parse(localStorage.getItem('dm_admin') || '{}').AdminId;
        request.Get(ipserv + 'api/etl/jobs', { adminId }, r => {
            if (r.success) setJobs(r.data);
            else Notify({ type: 'fail', text: 'Error fetching ETL Jobs' });
            setLoading(false);
        });
    };

    const fetchLogs = (jobId) => {
        setLogsLoading(true);
        request.Get(ipserv + 'api/etl/logs/' + jobId, { limit: 20 }, r => {
            if (r.success) setJobLogs(r.data);
            setLogsLoading(false);
        });
    };

    const handleSelectJob = (job) => {
        setSelectedJob(job);
        setFormData({
            JobId: job.JobId,
            JobName: job.JobName,
            SourceConnId: job.SourceConnId,
            TargetConnId: job.TargetConnId,
            SourceQuery: job.SourceQuery || '',
            AutqId: job.AutqId || '',
            TargetTable: job.TargetTable,
            OperationType: job.OperationType,
            CronSchedule: job.CronSchedule || '',
            IsActive: job.IsActive === 1,
            SourceEngine: job.SourceEngine || 'mysql'
        });
        setActiveTab('config');
        fetchLogs(job.JobId);
    };

    const handleNewJob = () => {
        setSelectedJob(null);
        setFormData({
            JobId: null,
            JobName: '',
            SourceConnId: '',
            TargetConnId: '',
            SourceQuery: '',
            AutqId: '',
            TargetTable: '',
            OperationType: 'INSERT',
            CronSchedule: '',
            IsActive: true,
            SourceEngine: 'mysql'
        });
        setJobLogs([]);
        setActiveTab('config');
    };

    const handleSave = () => {
        if (!formData.JobName || !formData.SourceConnId || !formData.TargetConnId || (!formData.SourceQuery && !formData.AutqId) || !formData.TargetTable) {
            Notify({ type: 'warning', text: 'Por favor, completa los campos requeridos (Nombre, Origen, Destino, Consulta/Autq y Tabla Destino).' });
            return;
        }

        const adminId = JSON.parse(localStorage.getItem('dm_admin') || '{}').AdminId;
        const payload = { ...formData, AdminId: adminId };

        if (formData.JobId) {
            request.Put(ipserv + 'api/etl/jobs/' + formData.JobId, payload, r => {
                if (r.success) {
                    Notify({ type: 'success', text: 'Job actualizado correctamente.' });
                    fetchJobs();
                } else {
                    Notify({ type: 'fail', text: 'Error al actualizar.' });
                }
            });
        } else {
            request.Post(ipserv + 'api/etl/jobs', payload, r => {
                if (r.success) {
                    Notify({ type: 'success', text: 'Job creado correctamente.' });
                    fetchJobs();
                    handleSelectJob({ ...payload, JobId: r.data.insertId });
                } else {
                    Notify({ type: 'fail', text: 'Error al crear job.' });
                }
            });
        }
    };

    const handleDelete = (id) => {
        if (window.confirm("¿Estás seguro de eliminar este Job ETL?")) {
            request.Delete(ipserv + 'api/etl/jobs/' + id, {}, r => {
                if (r.success) {
                    Notify({ type: 'success', text: 'Eliminado.' });
                    fetchJobs();
                    if (formData.JobId === id) handleNewJob();
                }
            });
        }
    };

    const handleExecute = () => {
        if (!selectedJob) return;
        setExecuting(true);
        request.Post(ipserv + 'api/etl/execute/' + selectedJob.JobId, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Ejecución iniciada en background.' });
                setTimeout(() => fetchLogs(selectedJob.JobId), 3000);
            } else {
                Notify({ type: 'fail', text: 'Error al ejecutar.' });
            }
            setExecuting(false);
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'SUCCESS': return <Badge bg="success" className="p-badge"><FontAwesomeIcon icon={faCheckCircle} /> SUCCESS</Badge>;
            case 'FAILED': return <Badge bg="danger" className="p-badge"><FontAwesomeIcon icon={faExclamationCircle} /> FAILED</Badge>;
            case 'RUNNING': return <Badge bg="warning" text="dark" className="p-badge"><Spinner size="sm" animation="border" className="me-1" /> RUNNING</Badge>;
            default: return <Badge bg="secondary" className="p-badge">{status}</Badge>;
        }
    };

    return (
        <div className="animate-in pb-4" style={{ color: 'var(--text-main)' }}>
            <div className="d-flex justify-content-between align-items-center mb-4 p-4 bg-white rounded-4 shadow-sm border border-light">
                <div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-3 rounded-4 bg-primary bg-opacity-10 text-primary">
                            <FontAwesomeIcon icon={faNetworkWired} size="lg" />
                        </div>
                        <div>
                            <h2 style={{ fontWeight: 800, margin: 0, color: 'var(--accent)', letterSpacing: '-0.02em' }}>
                                ETL Studio
                            </h2>
                            <p className="text-muted small mt-1 mb-0 opacity-75">Motor de Orquestación Inteligente: E-T-L Pipeline.</p>
                        </div>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Button 
                        onClick={handleNewJob} 
                        className="premium-btn-glass px-4 py-2"
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Nuevo Trabajo
                    </Button>
                    {formData.JobId && (
                        <Button 
                            className="premium-button px-4 py-2 shadow-lg" 
                            onClick={handleExecute} 
                            disabled={executing}
                            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                        >
                            <FontAwesomeIcon icon={executing ? faSpinner : faPlay} spin={executing} className="me-2" /> 
                            {executing ? 'Ejecutando...' : 'Ejecutar Ahora'}
                        </Button>
                    )}
                </div>
            </div>

            <Row className="g-4">
                {/* COLUMN 1: JOBS LIST */}
                <Col lg={3}>
                    <div className="sa-panel h-100 shadow-sm border-0">
                        <div className="sa-panel-header p-3 bg-light border-bottom">
                            <h6 className="m-0 font-weight-bold text-muted">Explorador de Jobs</h6>
                        </div>
                        <div className="sa-panel-body p-0" style={{ height: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                            {loading ? (
                                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                            ) : jobs.length === 0 ? (
                                <div className="text-center p-5 text-muted small">
                                    <FontAwesomeIcon icon={faBoxOpen} size="3x" className="mb-3 opacity-20" />
                                    <p>No hay trabajos configurados.</p>
                                </div>
                            ) : (
                                <div className="list-group list-group-flush p-2">
                                    {jobs.map(job => (
                                        <button
                                            key={job.JobId}
                                            className={`list-group-item list-group-item-action border-0 rounded-3 mb-2 py-3 px-3 transition-all ${formData.JobId === job.JobId ? 'bg-primary text-white shadow-sm' : 'bg-transparent'}`}
                                            onClick={() => handleSelectJob(job)}
                                        >
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{job.JobName}</span>
                                                <div className={`status-indicator-mini ${job.IsActive ? 'online' : 'offline'}`}></div>
                                            </div>
                                            <div className={`d-flex align-items-center gap-2 ${formData.JobId === job.JobId ? 'text-white opacity-75' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>
                                                <FontAwesomeIcon icon={faClock} />
                                                <span>{job.CronSchedule || 'Manual'}</span>
                                                <Badge bg={formData.JobId === job.JobId ? "light" : "primary"} text={formData.JobId === job.JobId ? "primary" : "white"} className="ms-auto border-0">{job.SourceEngine}</Badge>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Col>

                {/* COLUMN 2 & 3: EDITOR (Tabbed) */}
                <Col lg={9}>
                    <div className="sa-panel h-100 shadow-sm border-0">
                        <div className="sa-panel-header p-0 bg-light border-bottom">
                            <div className="d-flex justify-content-between align-items-center pr-3">
                                <Nav variant="tabs" className="border-0 px-3 pt-2" activeKey={activeTab} onSelect={k => setActiveTab(k)}>
                                    <Nav.Item>
                                        <Nav.Link eventKey="config" className="px-4 py-3 fw-bold"><FontAwesomeIcon icon={faCogs} className="me-2" /> General</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="source" className="px-4 py-3 fw-bold"><FontAwesomeIcon icon={faDatabase} className="me-2" /> Extracción (Source)</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="target" className="px-4 py-3 fw-bold"><FontAwesomeIcon icon={faServer} className="me-2" /> Carga (Target)</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="logs" className="px-4 py-3 fw-bold"><FontAwesomeIcon icon={faHistory} className="me-2" /> Historial</Nav.Link>
                                    </Nav.Item>
                                </Nav>
                                <div className="d-flex gap-3 pe-3 align-items-center">
                                    {formData.JobId && (
                                        <button 
                                            className="btn btn-link text-danger p-2 rounded-circle hover-bg-light transition-all" 
                                            onClick={() => handleDelete(formData.JobId)}
                                            title="Eliminar Job"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    )}
                                    <Button className="premium-button px-4 py-2" onClick={handleSave}>
                                        <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar Cambios
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="sa-panel-body p-4" style={{ height: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                            <Tab.Content>
                                {/* TAB 1: GENERAL CONFIG */}
                                <Tab.Pane eventKey="config" className={activeTab === 'config' ? 'show active' : ''}>
                                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                                        <Row className="mb-4">
                                            <Col md={8}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-bold">Nombre del Trabajo ETL</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        className="premium-input w-100"
                                                        placeholder="Ej: Sincronización de Ventas Diarias"
                                                        value={formData.JobName}
                                                        onChange={e => setFormData({ ...formData, JobName: e.target.value })}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-bold">Estado Operativo</Form.Label>
                                                    <div className="d-flex align-items-center gap-3 p-3 bg-light rounded border">
                                                        <Form.Check
                                                            type="switch"
                                                            id="active-switch-main"
                                                            label={formData.IsActive ? "Activo (Programado)" : "Inactivo (Manual)"}
                                                            checked={formData.IsActive}
                                                            onChange={e => setFormData({ ...formData, IsActive: e.target.checked })}
                                                            className="fw-bold"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold"><FontAwesomeIcon icon={faClock} className="me-2 text-primary" /> Frecuencia de Ejecución (Cron Expression)</Form.Label>
                                            <div className="d-flex gap-3 align-items-center">
                                                <Form.Control
                                                    type="text"
                                                    className="premium-input font-monospace"
                                                    placeholder="0 0 * * * (Ej: Cada medianoche)"
                                                    value={formData.CronSchedule}
                                                    onChange={e => setFormData({ ...formData, CronSchedule: e.target.value })}
                                                    style={{ maxWidth: '300px' }}
                                                />
                                                <small className="text-muted">
                                                    Utilice formato estándar de Cron (Minutos Horas Días Mes Días-Semana).
                                                    <a href="https://crontab.guru/" target="_blank" rel="noreferrer" className="ms-2 text-primary">Ver guía</a>
                                                </small>
                                            </div>
                                        </Form.Group>
                                    </motion.div>
                                </Tab.Pane>

                                {/* TAB 2: SOURCE CONFIG */}
                                <Tab.Pane eventKey="source" className={activeTab === 'source' ? 'show active' : ''}>
                                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                                        <Row className="mb-4">
                                            <Col md={8}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Conexión de Origen</Form.Label>
                                                    <Form.Select className="premium-input w-100" value={formData.SourceConnId} onChange={e => setFormData({ ...formData, SourceConnId: e.target.value })}>
                                                        <option value="">-- Seleccionar Base de Datos de Origen --</option>
                                                        {connections.map(c => <option key={c.ConnId} value={c.ConnId}>{c.ConnName} [{c.ConnDriver.toUpperCase()}]</option>)}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Tipo de Motor (Engine)</Form.Label>
                                                    <Form.Select className="premium-input w-100" value={formData.SourceEngine} onChange={e => setFormData({ ...formData, SourceEngine: e.target.value })}>
                                                        <option value="mysql">MySQL / MariaDB</option>
                                                        <option value="postgres">PostgreSQL</option>
                                                        <option value="mssql">SQL Server</option>
                                                        <option value="locU">DataManager Internal (locU)</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <hr className="my-4 opacity-10" />

                                        <div className="bg-light p-4 rounded border">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="fw-bold m-0"><FontAwesomeIcon icon={faCode} className="me-2 text-primary" /> Definición de la Extracción</h6>
                                                <Badge bg="primary">SQL / AutQ</Badge>
                                            </div>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="small fw-bold">Usar Consulta Guardada (AutQ)</Form.Label>
                                                        <Form.Select
                                                            className="premium-input"
                                                            value={formData.AutqId}
                                                            onChange={e => setFormData({ ...formData, AutqId: e.target.value, SourceQuery: '' })}
                                                        >
                                                            <option value="">-- Manual SQL Query --</option>
                                                            {queries.map(q => <option key={q.AutQId} value={q.AutQId}>{q.AutQTitle} (ID: {q.AutQId})</option>)}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6} className="d-flex align-items-center pt-3">
                                                    <small className="text-muted">Si selecciona una AutQ, el motor usará la definición del Query Builder automáticamente.</small>
                                                </Col>
                                            </Row>

                                            {!formData.AutqId && (
                                                <Form.Group className="mt-3">
                                                    <Form.Label className="small fw-bold">Query SQL Nativo</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={8}
                                                        className="font-monospace p-3"
                                                        style={{ fontSize: '0.85rem', backgroundColor: '#ffffff', border: '1px solid var(--card-border)' }}
                                                        placeholder="SELECT * FROM table WHERE condition..."
                                                        value={formData.SourceQuery}
                                                        onChange={e => setFormData({ ...formData, SourceQuery: e.target.value })}
                                                    />
                                                </Form.Group>
                                            )}
                                        </div>
                                    </motion.div>
                                </Tab.Pane>

                                {/* TAB 3: TARGET CONFIG */}
                                <Tab.Pane eventKey="target" className={activeTab === 'target' ? 'show active' : ''}>
                                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold">Conexión de Destino (DataManager Base)</Form.Label>
                                            <Form.Select className="premium-input w-100" value={formData.TargetConnId} onChange={e => setFormData({ ...formData, TargetConnId: e.target.value })}>
                                                <option value="">-- Seleccionar Base de Datos de Destino --</option>
                                                {connections.map(c => <option key={c.ConnId} value={c.ConnId}>{c.ConnName} [{c.ConnDriver.toUpperCase()}]</option>)}
                                            </Form.Select>
                                        </Form.Group>

                                        <div className="bg-light p-4 rounded border">
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-4">
                                                        <Form.Label className="fw-bold"><FontAwesomeIcon icon={faTable} className="me-2 text-success" /> Tabla de Destino</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            className="premium-input"
                                                            placeholder="Ej: ext_bi_ventas"
                                                            value={formData.TargetTable}
                                                            onChange={e => setFormData({ ...formData, TargetTable: e.target.value })}
                                                        />
                                                        <small className="text-muted mt-1 d-block">La tabla debe existir en el destino con las mismas columnas que el origen.</small>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-4">
                                                        <Form.Label className="fw-bold">Método de Integración</Form.Label>
                                                        <Form.Select className="premium-input" value={formData.OperationType} onChange={e => setFormData({ ...formData, OperationType: e.target.value })}>
                                                            <option value="INSERT">INSERT (Solo agregar registros)</option>
                                                            <option value="REPLACE">REPLACE (Actualizar si existe PK)</option>
                                                            <option value="TRUNCATE">TRUNCATE (Limpiar tabla antes de cargar)</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    </motion.div>
                                </Tab.Pane>

                                {/* TAB 4: LOGS */}
                                <Tab.Pane eventKey="logs" className={activeTab === 'logs' ? 'show active' : ''}>
                                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h6 className="m-0 fw-bold"><FontAwesomeIcon icon={faHistory} className="me-2 text-info" /> Registro de Actividad Reciente</h6>
                                            <Button variant="outline-secondary" size="sm" onClick={() => fetchLogs(formData.JobId)} disabled={logsLoading}>
                                                <FontAwesomeIcon icon={faSyncAlt} spin={logsLoading} className="me-2" /> Actualizar
                                            </Button>
                                        </div>

                                        {logsLoading ? (
                                            <div className="text-center py-5"><Spinner animation="border" variant="info" /></div>
                                        ) : jobLogs.length === 0 ? (
                                            <div className="text-center p-5 bg-light rounded border border-dashed">
                                                <FontAwesomeIcon icon={faHistory} size="3x" className="mb-3 opacity-20" />
                                                <p className="m-0 text-muted">No hay registros de ejecución para este trabajo.</p>
                                            </div>
                                        ) : (
                                            <div className="premium-table-container">
                                                <Table className="premium-table m-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Inicio</th>
                                                            <th>Fin</th>
                                                            <th>Estado</th>
                                                            <th>Procesados</th>
                                                            <th>Mensaje</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {jobLogs.map(log => (
                                                            <tr key={log.LogId}>
                                                                <td className="small">{new Date(log.StartTime).toLocaleString()}</td>
                                                                <td className="small">{log.EndTime ? new Date(log.EndTime).toLocaleString() : '--'}</td>
                                                                <td>{getStatusBadge(log.Status)}</td>
                                                                <td className="fw-bold">{log.RecordsProcessed.toLocaleString()}</td>
                                                                <td className="small text-danger" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.ErrorMessage || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </motion.div>
                                </Tab.Pane>
                            </Tab.Content>
                        </div>
                    </div>
                </Col>
            </Row>

            <style>{`
                .transition-all { transition: all 0.2s ease; }
                .border-dashed { border-style: dashed !important; }
                
                .premium-btn-glass {
                    background: rgba(0, 97, 255, 0.05);
                    border: 1px solid rgba(0, 97, 255, 0.2);
                    color: var(--primary) !important;
                    font-weight: 600;
                    border-radius: var(--radius-md);
                    transition: all 0.3s ease;
                }

                .premium-btn-glass:hover {
                    background: rgba(0, 97, 255, 0.1);
                    border-color: var(--primary);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 97, 255, 0.1);
                }

                .nav-tabs .nav-link { 
                    border: none; 
                    color: var(--text-muted); 
                    border-bottom: 3px solid transparent;
                    margin-bottom: -1px;
                    transition: all 0.3s ease;
                }
                .nav-tabs .nav-link.active { 
                    background: transparent !important; 
                    color: var(--primary) !important; 
                    border-bottom: 3px solid var(--primary); 
                    opacity: 1;
                }
                
                .status-indicator-mini {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #cbd5e1;
                }
                .status-indicator-mini.online { background: #10b981; box-shadow: 0 0 8px rgba(16, 185, 129, 0.5); }
                .status-indicator-mini.offline { background: #f43f5e; }

                .hover-bg-light:hover {
                    background-color: #f1f5f9;
                }

                .sa-panel {
                    border-radius: 20px !important;
                    overflow: hidden;
                    background: #ffffff;
                }

                .premium-table thead th {
                    background-color: #f8fafc !important;
                    color: var(--text-muted);
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 1rem !important;
                }
            `}</style>
        </div>
    );
};

export default EtlStudioPanel;
